import React, { memo } from 'react';
import templeJson from '../../../config/temple.json';
import { TextField, Button, Grid, Container, Typography, Box, Link as MuiLink } from '@mui/material';
import {
  LocationOn as LocationOnIcon,
  MailOutline as MailOutlineIcon,
  Phone as PhoneIcon,
  Facebook as FacebookIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  Language as LanguageIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useFooterFunctions } from './CustomHook';

function Footer() {
  const { onSubmit, register, handleSubmit, errors } = useFooterFunctions();

  const isStyleOne = templeJson['Footer']?.style === 1;
  const footerClass = isStyleOne ? 'footer' : 'footer-svt';

  const renderMenuItems = (items) =>
    items?.map((item, index) => {
      const isSitemap = item?.url === '/sitemap.xml';

      return (
        <MuiLink
          key={item?.url || item?.name || index}
          {...(isSitemap
            ? {
                href: item?.url, // ✅ direct file open
                rel: 'noopener noreferrer'
              }
            : {
                component: Link, // ✅ normal React routing
                to: item?.url
              })}
          underline="none"
          color="inherit"
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 1,
            color: isStyleOne ? 'text.primary' : 'common.white'
          }}
        >
          <KeyboardArrowRightIcon sx={{ mr: '3px' }} /> {item?.name}
        </MuiLink>
      );
    });

  return (
    <>
      <Box
        className={`container-fluid ${footerClass}`}
        sx={
          isStyleOne
            ? {
                background: `linear-gradient(rgba(187,157,165,0.9), rgba(187,157,165,0.9)), url(${process.env.REACT_APP_S3_BASE_URL}/api/images/footer-bg.jpg)`,
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
              }
            : {
                background: '#212529',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover'
              }
        }
      >
        <Container sx={{ mt: 4, paddingBottom: 2.5 }}>
          <Grid container spacing={5}>
            <Grid item xs={12} sm={6} md={3}>
              <Box display="flex" flexDirection="column">
                <Typography variant="h6" mb={2} color={isStyleOne ? 'primary' : 'common.white'}>
                  Newsletter
                </Typography>
                <Typography sx={{ fontSize: '14.5px' }} color={isStyleOne ? 'greyMain' : 'common.white'} mb={1.2}>
                  Please register your email address to be connected with temple and receive regular newsletters and events.
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <TextField
                    InputLabelProps={{
                      sx: {
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        padding: '0 6px',
                        '& .MuiOutlinedInput-input': {},
                        color: '#828282',
                        '&.Mui-focused': {
                          color: '#e42100'
                        }
                      }
                    }}
                    sx={{
                      color: '#111',
                      mb: '3px',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff'
                    }}
                    fullWidth
                    label="First Name*"
                    {...register('first_name', {
                      required: 'First Name is required'
                    })}
                    error={!!errors.first_name}
                    helperText={errors.first_name?.message}
                  />
                  <TextField
                    InputLabelProps={{
                      sx: {
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        padding: '0 6px',
                        '& .MuiOutlinedInput-input': {},
                        color: '#828282',
                        '&.Mui-focused': {
                          color: '#e42100'
                        }
                      }
                    }}
                    sx={{
                      color: '#111',
                      mb: '3px',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff'
                    }}
                    fullWidth
                    label="Last Name*"
                    variant="outlined"
                    {...register('last_name', {
                      required: 'Last Name is required'
                    })}
                    error={!!errors.last_name}
                    helperText={errors.last_name?.message}
                    margin="dense"
                  />
                  <TextField
                    InputLabelProps={{
                      sx: {
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        padding: '0 6px',
                        '& .MuiOutlinedInput-input': {},
                        color: '#828282',
                        '&.Mui-focused': {
                          color: '#e42100'
                        }
                      }
                    }}
                    sx={{
                      color: '#111',
                      mb: '3px',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff'
                    }}
                    fullWidth
                    label="Email*"
                    variant="outlined"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^\S+@\S+$/i,
                        message: 'Invalid email'
                      }
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    margin="dense"
                  />
                  <TextField
                    InputLabelProps={{
                      sx: {
                        backgroundColor: '#ffffff',
                        borderRadius: '4px',
                        padding: '0 6px',
                        '& .MuiOutlinedInput-input': {},
                        color: '#828282',
                        '&.Mui-focused': {
                          color: '#e42100'
                        }
                      }
                    }}
                    sx={{
                      color: '#111',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff'
                    }}
                    fullWidth
                    label="Phone"
                    variant="outlined"
                    {...register('contact_number')}
                    error={!!errors.contact_number}
                    helperText={errors.contact_number?.message}
                    margin="dense"
                  />
                  <Button variant="contained" color="primary" fullWidth type="submit" sx={{ mt: '4px', padding: '12px 18px' }}>
                    Submit
                  </Button>
                </form>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="h6" mb={2} color={isStyleOne ? 'primary' : 'common.white'}>
                  Information
                </Typography>
                <Box sx={{ fontSize: '14.5px' }}>{renderMenuItems(templeJson['Footer']?.Information?.menuItems)}</Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="h6" mb={2} color={isStyleOne ? 'primary' : 'common.white'}>
                  Others
                </Typography>
                <Box sx={{ fontSize: '14.5px' }}>{renderMenuItems(templeJson['Footer']?.Others?.menuItems)}</Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="h6" mb={2} color={isStyleOne ? 'primary' : 'common.white'}>
                  Contact Info
                </Typography>
                <Box display="flex" alignItems="flex-start" mb={1}>
                  <LocationOnIcon sx={{ mr: 1, color: '#fff' }} />
                  <Typography sx={{ fontSize: '14.5px' }} color={isStyleOne ? 'text.primary' : 'common.white'}>
                    {templeJson['Footer']?.ContactInfo?.address}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="flex-start" mb={1}>
                  <MailOutlineIcon sx={{ mr: 1, color: '#fff' }} />
                  <MuiLink
                    component={Link}
                    to={`mailto:${templeJson['Footer']?.ContactInfo?.email}`}
                    underline="none"
                    color="inherit"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      color: isStyleOne ? 'text.primary' : 'common.white'
                    }}
                  >
                    <Typography color={isStyleOne ? 'text.primary' : 'common.white'}>{templeJson['Footer']?.ContactInfo?.email}</Typography>
                  </MuiLink>
                </Box>
                <Box display="flex" alignItems="center" mb={2}>
                  <PhoneIcon sx={{ mr: 1, color: '#fff' }} />
                  <Typography color={isStyleOne ? 'text.primary' : 'common.white'}>{templeJson['Footer']?.ContactInfo?.phone}</Typography>
                </Box>

                {templeJson['Footer']?.WebsiteIssues?.url && (
                  <>
                    <Typography variant="h6" mb={1} color={isStyleOne ? 'primary' : 'common.white'}>
                      Website issues
                    </Typography>
                    <Box display="flex" alignItems="center" mb={2}>
                      <LanguageIcon sx={{ mr: 1, color: '#fff' }} />
                      <Typography color={isStyleOne ? 'text.primary' : 'common.white'}>
                        {templeJson['Footer']?.WebsiteIssues?.url}
                      </Typography>
                    </Box>
                  </>
                )}

                <Typography variant="h6" mb={1} color={isStyleOne ? 'primary' : 'common.white'}>
                  Follow Us
                </Typography>
                <Box display="flex" gap={2}>
                  {templeJson['Footer']?.FollowUs?.icons?.map((item, index) => (
                    <MuiLink
                      key={item?.url || item?.name || index}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      color="inherit"
                      aria-label={item?.name || 'Social media'}
                    >
                      {item.name === 'Facebook' ? (
                        <FacebookIcon sx={{ color: '#fff' }} />
                      ) : item.name === 'Youtube' ? (
                        <YouTubeIcon sx={{ color: '#fff' }} />
                      ) : item.name === 'Instagram' ? (
                        <InstagramIcon sx={{ color: '#fff' }} />
                      ) : null}
                    </MuiLink>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box
        className={isStyleOne ? 'container-fluid copyright py-2' : 'container-fluid copyright-svt py-2'}
        sx={{
          backgroundColor: isStyleOne ? 'var(--primaryMain)' : '#1c2023',
          color: '#fff'
        }}
      >
        <Container>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Grid item xs={12} md={6} textAlign={{ xs: 'center', md: 'left' }}>
              <Typography>
                Copyright © 2026{' '}
                <MuiLink
                  sx={{ textDecoration: 'none' }}
                  underline="hover"
                  component={Link}
                  to="/"
                  color={isStyleOne ? 'common.white' : 'common.white'}
                >
                  {templeJson['templeName']}
                </MuiLink>
                , All rights reserved.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6} textAlign={{ xs: 'center', md: 'right' }}>
              <Typography>
                Designed & Developed by{' '}
                <MuiLink
                  sx={{ textDecoration: 'none' }}
                  href={templeJson['Footer']?.CopyRight?.designedby?.url}
                  underline="hover"
                  color={isStyleOne ? 'common.white' : 'common.white'}
                >
                  {templeJson['Footer']?.CopyRight?.designedby?.name}
                </MuiLink>
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default memo(Footer);
